/**
 * Index Map — which indices work on which satellite.
 * Landsat cannot use Sentinel-2 red-edge indices.
 */

/** Actual sensors used for heatmap / scene cards */
export type IndexMapSatellite = 'S2' | 'S1' | 'L8';

/** Observation-bar filter: ALL merges S2 + Landsat dates by default for optical indices */
export type IndexMapSatelliteFilter = IndexMapSatellite | 'ALL';

export interface IndexMapOption {
  value: string;
  label: string;
  /** Satellites that can compute this index */
  satellites: IndexMapSatellite[];
}

export const INDEX_MAP_OPTIONS: IndexMapOption[] = [
  { value: 'ndvi', label: 'NDVI: crop greenness', satellites: ['S2', 'L8'] },
  { value: 'evi', label: 'EVI: crop greenness', satellites: ['S2', 'L8'] },
  { value: 'msavi2', label: 'MSAVI2: greenness on bare soil', satellites: ['S2', 'L8'] },
  { value: 'sipi', label: 'SIPI: leaf pigment stress', satellites: ['S2', 'L8'] },
  { value: 'savi', label: 'SAVI: greenness, soil adjusted', satellites: ['S2', 'L8'] },
  { value: 'vari', label: 'VARI: visible greenness', satellites: ['S2', 'L8'] },
  { value: 'arvi', label: 'ARVI: greenness through haze', satellites: ['S2', 'L8'] },
  { value: 'rgr', label: 'RGR: red-to-green ratio', satellites: ['S2', 'L8'] },
  { value: 'ndii', label: 'NDII: canopy water', satellites: ['S2', 'L8'] },
  { value: 'ndmi', label: 'NDMI: crop moisture', satellites: ['S2', 'L8'] },
  { value: 'ndwi', label: 'NDWI: surface water', satellites: ['S2', 'L8'] },
  { value: 'nbr', label: 'NBR: burn signal', satellites: ['S2', 'L8'] },
  { value: 'psri', label: 'PSRI: crop aging', satellites: ['S2'] },
  { value: 'rendvi', label: 'RENDVI: red-edge greenness', satellites: ['S2'] },
  { value: 'ireci', label: 'IRECI: chlorophyll', satellites: ['S2'] },
  { value: 's2rep', label: 'S2REP: red-edge position', satellites: ['S2'] },
  { value: 'reb_ndvi1', label: 'RE NDVI: red-edge greenness', satellites: ['S2'] },
  { value: 'sccci', label: 'SCCCI: canopy chlorophyll', satellites: ['S2'] },
  { value: 'ndpi', label: 'NDPI: radar surface', satellites: ['S1'] },
  { value: 'rvi', label: 'RVI: radar vegetation', satellites: ['S1'] },
  { value: 'radar_soil_moisture', label: 'Radar soil moisture', satellites: ['S1'] },
];

export const SATELLITE_LABELS: Record<IndexMapSatelliteFilter, string> = {
  ALL: 'All (S2 + Landsat)',
  S2: 'Sentinel-2',
  S1: 'Sentinel-1',
  L8: 'Landsat 8/9',
};

export function satellitesForIndex(index: string): IndexMapSatellite[] {
  const row = INDEX_MAP_OPTIONS.find((o) => o.value === index);
  return row?.satellites?.length ? [...row.satellites] : ['S2'];
}

/** Dropdown options for the observation bar (includes ALL when S2+L8 both apply). */
export function satelliteFilterOptionsForIndex(index: string): IndexMapSatelliteFilter[] {
  const sats = satellitesForIndex(index);
  if (sats.includes('S2') && sats.includes('L8')) {
    return ['ALL', 'S2', 'L8'];
  }
  return [...sats];
}

/** Default observation filter: ALL when both optical sensors apply. */
export function defaultSatelliteForIndex(index: string): IndexMapSatelliteFilter {
  const opts = satelliteFilterOptionsForIndex(index);
  if (opts.includes('ALL')) return 'ALL';
  return opts[0] ?? 'S2';
}

/** Sensor used for /indexheatmap/ — never ALL. */
export function resolveHeatmapSatellite(
  filter: IndexMapSatelliteFilter,
  sceneSatellite?: string | null
): IndexMapSatellite {
  if (filter === 'ALL') {
    const s = (sceneSatellite || 'S2').toUpperCase();
    if (s === 'L8' || s === 'S1' || s === 'S2') return s;
    return 'S2';
  }
  return filter;
}

export function indexOptionsForSatellite(satellite: IndexMapSatellite): IndexMapOption[] {
  return INDEX_MAP_OPTIONS.filter((o) => o.satellites.includes(satellite));
}

export function isIndexAllowedOnSatellite(index: string, satellite: IndexMapSatellite): boolean {
  return satellitesForIndex(index).includes(satellite);
}
